import type { AnalyticsProperties } from '@strava/container-context';
import type {
  CameraTarget,
  CameraViewType,
  Language,
  MapType,
  MeasurementUnitSystem,
  RouteWaypoint,
  TerrainEngine,
  VersionInfo
} from '@strava/mre';

export type LatLng = [number, number];

// marker type names only exist in this codebase, does not equate to names in FatmapMobileSDK
export type MarkerType = 'route-scrub';

export type LatLngElevation = [number, number, number];

export type LocationStream = Array<{ lat: number; lng: number }>;

export type ElevationStream = number[];

export type DistanceStream = number[];

// We show the description in the React wrapper layer around the MRE; the MRE doesn't know
// about it (and nor do its type definitions)!
export type RouteWaypointWithDescription = RouteWaypoint & {
  description?: string;
};

export interface FallbackImage {
  url: string;
  width?: number;
  height?: number;
}

export enum ZoomSpeed {
  ZoomIn = 1,
  ZoomOut = -1,
  ZoomStop = 0
}

export interface MREContextType {
  page: string | null; // Used for analytics events
  mapContainerRef: React.RefObject<HTMLDivElement>; // The container element for the MRE
  terrainEngine?: TerrainEngine | null;
  setTerrainEngine: (terrainEngine: TerrainEngine) => void;
  versionInfo: VersionInfo | null;
  setVersionInfo: (versionInfo: VersionInfo) => void;
  track: (fields: AnalyticsProperties) => void;
  t: (key: string, options?: Record<string, string | number>) => string;
}

export interface MREStateContextType {
  polyline: LatLng[] | null;
  setPolyline: (polyline: LatLng[]) => void;
  mapboxToken: string | null;
  setMapboxToken: (mapboxToken: string) => void;
  surfaceIdentifier: string | null;
  setSurfaceIdentifier: (surfaceIdentifier: string) => void;
  mapType: MapType | null;
  setMapType: (mapType: MapType) => void;
  showPOIs: boolean;
  setShowPOIs: (showPOIs: boolean) => void;
  desiredLanguage?: Language | null;
  setDesiredLanguage: (desiredLanguage: Language) => void;
  unitSystem: MeasurementUnitSystem | null;
  setUnitSystem: (unitSystem: MeasurementUnitSystem) => void;
  cameraViewType: CameraViewType | null;
  setCameraViewType: (cameraViewType: CameraViewType) => void;
  zoomSpeed?: number;
  setZoomSpeed: (zoomSpeed: number) => void;
  pageIsUnloadingRef: React.MutableRefObject<boolean>; // Ref to track if the page is unloading
  isFlyoverInProgress: boolean;
  setIsFlyoverInProgress: (isFlyoverInProgress: boolean) => void;
  resetCameraAndContent: () => void;
  flyoverEndTargetRef?: React.MutableRefObject<CameraTarget | undefined>; // Ref to track the end target of the current flyover, if any
  showPOIsOnFlyoverCompleteRef?: React.MutableRefObject<boolean | undefined>; // Ref to track whether to show POIs when the current flyover completes, if any
}

export type FlyoverPlaybackState = 'playing' | 'paused' | 'finished';

export interface PrivacyZone {
  position: {
    lat: number;
    lng: number;
  };
  radius: number; // in meters
}

export interface ScreenPosition {
  x: number;
  y: number;
  isOccluded?: boolean;
}
